import { 
  Building2, 
  Plane, 
  DollarSign, 
  AlertTriangle, 
  ChevronRight, 
  Search,
  Plus,
  ArrowRight,
  MoreHorizontal,
  FileText
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

export function DashboardMVPMock() {
  return (
    <div className="flex flex-col min-h-screen bg-slate-50/50">
      
      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <div className="mr-4 flex">
            <a className="mr-6 flex items-center space-x-2" href="#">
              <span className="hidden font-bold sm:inline-block">PlaneMAX</span>
            </a>
            <nav className="flex items-center space-x-6 text-sm font-medium">
              <span className="transition-colors hover:text-foreground/80 text-foreground font-bold">Dashboard</span>
              <span className="transition-colors hover:text-foreground/80 text-muted-foreground">Schedule</span>
              <span className="transition-colors hover:text-foreground/80 text-muted-foreground">Ramp</span>
              <span className="transition-colors hover:text-foreground/80 text-muted-foreground">Contracts</span>
            </nav>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 space-y-6 container max-w-7xl mx-auto">
        
        {/* Page Title & Location Context */}
        <div className="flex justify-between items-center mb-2">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Capacity Command Center</h1>
            <p className="text-muted-foreground">KSUA — Stuart Jet Center</p>
          </div>
          <div className="flex space-x-2">
            {/* Location Selector Placeholder */}
          </div>
        </div>

        {/* Row 1: Counter Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          
          {/* Card 1: Aircraft On Site */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                <span className="flex items-center gap-2"><Plane className="h-4 w-4" /> ON SITE</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">14</div>
              <p className="text-xs text-muted-foreground">
                of 22 based
              </p>
            </CardContent>
          </Card>

          {/* Card 2: Occupancy */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> OCCUPANCY</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">82%</div>
              <p className="text-xs text-muted-foreground">
                45,200 / 58,000 sqft
              </p>
              <Progress value={82} className="h-1 mt-2 bg-slate-100" indicatorClassName="bg-green-600" />
            </CardContent>
          </Card>

          {/* Card 3: Contracted Revenue */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                <span className="flex items-center gap-2"><DollarSign className="h-4 w-4" /> CONTRACTED REVENUE</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">$47,200<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
              <p className="text-xs text-muted-foreground">
                ($566,400 / yr)
              </p>
            </CardContent>
          </Card>

          {/* Card 4: Attention Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> ATTENTION ITEMS</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">5</div>
              <p className="text-xs text-muted-foreground">
                2 urgent · 3 warning
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Medium Widgets */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
          
          {/* Left Widget: Building Occupancy Breakdown (Costs 4 cols) */}
          <Card className="col-span-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="space-y-1">
                <CardTitle>Building Occupancy</CardTitle>
                <p className="text-sm text-muted-foreground">Space utilization by hangar</p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs">
                View Report <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hangar A */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="font-medium">Hangar A</div>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span>$18k/mo</span>
                    <span className="font-bold text-foreground">82%</span>
                  </div>
                </div>
                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-slate-800 w-[82%]"></div>
                </div>
              </div>

               {/* Hangar B */}
               <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="font-medium">Hangar B</div>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span>$14k/mo</span>
                    <span className="font-bold text-foreground">95%</span>
                  </div>
                </div>
                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-slate-800 w-[95%]"></div>
                </div>
              </div>

               {/* Hangar C */}
               <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="font-medium">Hangar C</div>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span>$8k/mo</span>
                    <span className="font-bold text-amber-600">52%</span>
                  </div>
                </div>
                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-slate-800 w-[52%]"></div>
                </div>
              </div>

               {/* Tiedowns */}
               <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="font-medium">Tiedowns</div>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span>$7k/mo</span>
                    <span className="font-bold text-foreground">71%</span>
                  </div>
                </div>
                <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-slate-400 w-[71%]"></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Widget: Expiring Contracts (Takes 3 cols) */}
          <Card className="col-span-3 border-l-4 border-l-amber-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  Expiring Contracts
                </CardTitle>
                <p className="text-sm font-medium text-amber-600">$12,400/mo at risk (90 days)</p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                
                {/* Item 1 - Urgent */}
                <div className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer group -mx-2 bg-red-50/50">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-red-500"></div>
                    <div>
                      <div className="text-sm font-medium">ABC Aviation</div>
                      <div className="text-xs text-muted-foreground">Hangar A-3</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-red-600">18 days</div>
                    <div className="text-xs text-muted-foreground">$4,200</div>
                  </div>
                </div>

                <Separator className="my-1 opacity-50" />

                {/* Item 2 - Warning */}
                <div className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer group -mx-2 bg-amber-50/30">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-amber-500"></div>
                    <div>
                      <div className="text-sm font-medium">XYZ Corp</div>
                      <div className="text-xs text-muted-foreground">Hangar B-1</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-amber-600">45 days</div>
                    <div className="text-xs text-muted-foreground">$5,800</div>
                  </div>
                </div>
                
                <Separator className="my-1 opacity-50" />

                {/* Item 3 - Info */}
                <div className="flex items-center justify-between p-2 rounded hover:bg-slate-50 cursor-pointer group -mx-2">
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-slate-300"></div>
                    <div>
                      <div className="text-sm font-medium">Smith Air</div>
                      <div className="text-xs text-muted-foreground">Tiedown T-12</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-600">78 days</div>
                    <div className="text-xs text-muted-foreground">$2,400</div>
                  </div>
                </div>

              </div>
              
              <div className="mt-4 pt-4 border-t text-center">
                <Button variant="link" size="sm" className="h-auto p-0 text-muted-foreground">
                  View all contracts <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>

            </CardContent>
          </Card>
        </div>

        {/* Row 3: Space Map Schematic */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-4">
             <div className="space-y-1">
                <CardTitle>Facility Overview</CardTitle>
                <p className="text-sm text-muted-foreground">Schematic view · <span className="text-green-600 font-medium">● Active</span> <span className="text-blue-500 font-medium">● Transient</span> <span className="text-amber-500 font-medium">● Expiring</span> <span className="text-slate-300 font-medium">● Vacant</span></p>
              </div>
            <Button variant="outline" size="sm">
              <Search className="mr-2 h-3 w-3" />
              Find Unit
            </Button>
          </CardHeader>
          <CardContent className="space-y-8">
            
            {/* Hangar A Row */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Hangar A</h3>
              <div className="grid grid-cols-6 gap-2">
                {/* A-1 */}
                <div className="h-20 bg-green-50 border border-green-200 rounded p-2 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-green-800">A-1</span>
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <div className="text-xs font-bold truncate">N123AB</div>
                    <div className="text-[10px] text-muted-foreground">G650</div>
                  </div>
                </div>
                {/* A-2 */}
                <div className="h-20 bg-green-50 border border-green-200 rounded p-2 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-green-800">A-2</span>
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <div className="text-xs font-bold truncate">N456CD</div>
                    <div className="text-[10px] text-muted-foreground">PC-12</div>
                  </div>
                </div>
                 {/* A-3 (Expiring) */}
                 <div className="h-20 bg-amber-50 border border-amber-200 rounded p-2 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all relative overflow-hidden">
                   <div className="absolute top-0 right-0 w-3 h-3 bg-amber-500 -mr-1.5 -mt-1.5 rotate-45"></div>
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-amber-800">A-3</span>
                    <div className="h-2 w-2 rounded-full bg-amber-500"></div>
                  </div>
                  <div>
                    <div className="text-xs font-bold truncate">N789EF</div>
                    <div className="text-[10px] text-muted-foreground">CJ3+</div>
                  </div>
                </div>
                 {/* A-4 (Vacant) */}
                 <div className="h-20 bg-slate-50 border border-dashed border-slate-300 rounded p-2 flex flex-col justify-between hover:bg-slate-100 cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-slate-400">A-4</span>
                    <div className="h-2 w-2 rounded-full border border-slate-300"></div>
                  </div>
                  <div className="flex items-center justify-center h-full">
                    <span className="text-[10px] text-slate-400 font-medium">VACANT</span>
                  </div>
                </div>
                {/* A-5 */}
                <div className="h-20 bg-green-50 border border-green-200 rounded p-2 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-green-800">A-5</span>
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <div className="text-xs font-bold truncate">N321GH</div>
                    <div className="text-[10px] text-muted-foreground">Phenom</div>
                  </div>
                </div>
                 {/* A-6 */}
                 <div className="h-20 bg-green-50 border border-green-200 rounded p-2 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-green-800">A-6</span>
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  </div>
                  <div>
                    <div className="text-xs font-bold truncate">N654IJ</div>
                    <div className="text-[10px] text-muted-foreground">C525</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hangar B Row (Larger units) */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Hangar B</h3>
              <div className="grid grid-cols-3 gap-2">
                {/* B-1 */}
                <div className="h-24 bg-green-50 border border-green-200 rounded p-3 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-bold text-green-800">B-1</span>
                    <div className="h-2.5 w-2.5 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-sm font-bold truncate">N111KL</div>
                      <div className="text-xs text-muted-foreground">G550</div>
                    </div>
                     <Badge variant="outline" className="bg-white/50 text-xs font-normal">Active</Badge>
                  </div>
                </div>
                 {/* B-2 (Expiring) */}
                 <div className="h-24 bg-amber-50 border border-amber-200 rounded p-3 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-bold text-amber-800">B-2</span>
                    <div className="h-2.5 w-2.5 rounded-full bg-amber-500"></div>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-sm font-bold truncate">N222MN</div>
                      <div className="text-xs text-muted-foreground">Falcon 2000</div>
                    </div>
                     <Badge variant="outline" className="bg-white/50 text-xs font-normal border-amber-300 text-amber-700">Expiring</Badge>
                  </div>
                </div>
                 {/* B-3 (Transient) */}
                 <div className="h-24 bg-blue-50 border border-blue-200 rounded p-3 flex flex-col justify-between hover:shadow-md cursor-pointer transition-all">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-bold text-blue-800">B-3</span>
                    <div className="h-2.5 w-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-sm font-bold truncate">N999TR</div>
                      <div className="text-xs text-muted-foreground">Global 6000</div>
                    </div>
                     <Badge variant="outline" className="bg-white/50 text-xs font-normal border-blue-300 text-blue-700">Transient</Badge>
                  </div>
                </div>
              </div>
            </div>

             {/* Footer Legend */}
             <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2">
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500"></div> Active Lease</div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Transient</div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500"></div> Expiring Soon</div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full border border-slate-400 bg-transparent"></div> Vacant</div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-slate-800"></div> Maintenance</div>
             </div>
          </CardContent>
        </Card>

        {/* Row 4: Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
           <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 hover:bg-slate-50 hover:border-slate-300">
             <div className="h-8 w-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center">
                <Building2 className="h-4 w-4" />
             </div>
             <div className="text-center">
                <div className="font-semibold text-sm">Open 3D Ramp</div>
                <div className="text-xs text-muted-foreground">View visual layout</div>
             </div>
           </Button>

           <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 hover:bg-slate-50 hover:border-slate-300">
             <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                <Plus className="h-4 w-4" />
             </div>
             <div className="text-center">
                <div className="font-semibold text-sm">New Aircraft</div>
                <div className="text-xs text-muted-foreground">Register aircraft</div>
             </div>
           </Button>

           <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 hover:bg-slate-50 hover:border-slate-300">
             <div className="h-8 w-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <FileText className="h-4 w-4" />
             </div>
             <div className="text-center">
                <div className="font-semibold text-sm">New Contract</div>
                <div className="text-xs text-muted-foreground">Create lease</div>
             </div>
           </Button>

            <Button variant="outline" className="h-auto py-4 flex flex-col gap-2 hover:bg-slate-50 hover:border-slate-300">
             <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center">
                <MoreHorizontal className="h-4 w-4" />
             </div>
             <div className="text-center">
                <div className="font-semibold text-sm">All Contracts</div>
                <div className="text-xs text-muted-foreground">View portfolio</div>
             </div>
           </Button>
        </div>

      </main>
    </div>
  )
}
